import { forwardRef } from "react";
import { Button } from "@/ui/button";
import { Spinner } from "@/ui/spinner";
import { Textarea } from "@/ui/textarea";
import { ICONES, Icone } from "../visual.jsx";
import { useCelular } from "../estado.jsx";

/**
 * A BARRA DE ESCREVER da casa — uma só, para toda superfície onde se escreve.
 *
 * Eram três: a do atendimento (cartão, texto em cima, controles embaixo), a da
 * conversa da peça (linha com textarea cru de borda própria) e a dos
 * assistentes (campo solto com a fileira de botões separada). Mesma tarefa,
 * três alturas de botão, três raios, três cores de envio e três teclados —
 * quem escreve o dia inteiro atravessa as três e reaprende a barra em cada
 * uma. O desenho que ficou é o do atendimento, porque é o que aguenta barra
 * cheia: o texto tem a LARGURA TODA e a barra tem a LINHA TODA, então ícone
 * novo não come o campo.
 *
 * Dois grupos e nada escrito entre eles: o que ENTRA no texto (`esquerda`) e o
 * que MANDA (`direita` + o botão de envio, que é sempre desta peça). Legenda de
 * teclado não existe aqui — quem escreve aperta Enter na segunda mensagem da
 * vida e não lê aquilo de novo nunca.
 */

/**
 * A medida de TODO controle da barra.
 *
 * Uma medida só para clipe, microfone e envio: com três (34, 42, 44) os ícones
 * ficavam cinco pixels abaixo do meio e o microfone menor que o envio, na mesma
 * fileira.
 *
 * 36 no computador e 44 no celular — era o contrário (42 e 36), o botão maior
 * justo onde há mouse e o menor onde há dedo. No celular a barra perde um ícone
 * para o menu "Mais" (ver `Redacao.jsx`), e é isso que paga os 44.
 */
export const ALTURA_DO_COMPOSER = 36;
export const ALTURA_DO_COMPOSER_CELULAR = 44;

/*
 * O botão redondo da barra.
 *
 * `variant="ghost"` com o hover do registro trocado pelo da casa: o `hover:bg-muted`
 * do preset acenderia um cinza diferente do véu de 8% que o resto da mesa usa.
 * O `hover:bg-transparent` que estava aqui apagava o realce inteiro — utilitário
 * ganha do `.lin:hover`, que mora em `@layer base` —, e a barra era a única
 * fileira de botões da mesa que não respondia ao ponteiro.
 */
export const BOTAO_DO_COMPOSER =
  "lin size-[var(--composer)] flex-none rounded-full border-none bg-transparent p-0 text-tinta-suave transition-[background-color,color,transform] duration-[var(--t-curta)] ease-entra hover:bg-foreground/8 hover:text-tinta";

/** O botão redondo da barra — anexo, nota, microfone, sugestão. */
export function BotaoDoComposer({
  icone,
  dica,
  rotulo = dica,
  aoTocar,
  tamanho = 20,
  marcado = false,
  fundo,
  children,
  ...resto
}) {
  return (
    <Button
      variant="ghost"
      onClick={aoTocar}
      data-dica={dica}
      /* `data-dica` NÃO é nome acessível: botão só de ícone continua com
         `aria-label`. */
      aria-label={rotulo}
      aria-pressed={marcado || undefined}
      className={BOTAO_DO_COMPOSER}
      /* O véu fica no `style`, e não em classe, porque o `hover:bg-foreground/8`
         ganharia dele e o apagaria justamente ao passar o mouse no botão
         LIGADO. */
      style={fundo ? { background: fundo } : undefined}
      {...resto}
    >
      {children ?? <Icone de={icone} tamanho={tamanho} />}
    </Button>
  );
}

/*
 * O envio por DESTINO. Terracota é o que sai para o cliente; âmbar é a nota,
 * que fica na casa — cor diferente aqui é promessa de destino diferente. A
 * letra é o token do tema (`sobre-terra`, `sobre-ambar`), e não `creme`: no
 * escuro o terra clareia e o creme em cima dele deixava de ser lido.
 *
 * O hover mistura 12% da tinta do tema no fundo: escurece no claro e clareia no
 * escuro, com a mesma conta para os dois destinos.
 */
const ENVIO = {
  acao: {
    pele: "bg-terra text-sobre-terra hover:bg-[color-mix(in_oklab,var(--color-terra)_88%,var(--color-foreground))] hover:text-sobre-terra",
    fundo: "var(--color-terra)",
    letra: "var(--color-sobre-terra)",
  },
  nota: {
    pele: "bg-ambar text-sobre-ambar hover:bg-[color-mix(in_oklab,var(--color-ambar)_88%,var(--color-foreground))] hover:text-sobre-ambar",
    fundo: "var(--color-ambar)",
    letra: "var(--color-sobre-ambar)",
  },
};

/*
 * O foco da barra, por destino. O campo de dentro não tem borda nem anel (ele é
 * o cartão inteiro), então quem mostra "o cursor está aqui" é a MOLDURA: a
 * borda do tom a meio caminho e o halo de 3px, o mesmo idioma de campo em foco
 * do resto da mesa. Sem isto, com dois cartões na tela (conversa e peça), nada
 * dizia em qual deles o Enter ia cair.
 */
const FOCO = {
  acao: "focus-within:border-terra/50 focus-within:shadow-[0_0_0_3px_color-mix(in_oklab,var(--color-terra)_12%,transparent),0_3px_14px_color-mix(in_oklab,var(--color-foreground)_4%,transparent)]",
  nota: "focus-within:border-ambar/60 focus-within:shadow-[0_0_0_3px_color-mix(in_oklab,var(--color-ambar)_14%,transparent),0_3px_14px_color-mix(in_oklab,var(--color-foreground)_4%,transparent)]",
};

export const Composer = forwardRef(function Composer(
  {
    valor,
    aoMudar,
    aoEnviar,
    convite,
    rotulo,
    /* O que ENTRA no texto (anexo, menu, nota) e o que MANDA junto do envio
       (contador, microfone, parar). */
    esquerda,
    direita,
    /* Dentro do cartão, acima do texto: citação, trecho marcado, anexo
       pendurado — o que o envio vai levar junto. */
    acima,
    enviando = false,
    travado = false,
    dicaDoEnvio = "Enviar",
    iconeDoEnvio = ICONES.enviar,
    /* `acao` (vai ao cliente, terracota) ou `nota` (fica na casa, âmbar): muda
       o envio e o foco da moldura juntos. */
    tomDoEnvio = "acao",
    tomDaBorda = "border-borda-controle",
    /* `hidden` sozinho não some com um `display:flex` — a classe precisa vir
       junto, e é por isso que isto é prop e não o atributo cru. */
    oculto = false,
    aoTeclar,
    campoProps,
    ...resto
  },
  ref,
) {
  const celular = useCelular();
  const envio = ENVIO[tomDoEnvio] ?? ENVIO.acao;
  /* Vazio é `disabled` de verdade, e a regra de desabilitado da casa (fora de
     camada, `index.css`) pinta o fundo apagado e a letra fraca — que é o cinza
     que se quer aqui. Só a borda tracejada sai: num círculo de 36px ela vira
     serrilhado, não sinal. Saindo, o botão também está `disabled`, mas continua
     na cor do destino com a roda dentro: é o envio em voo, não a falta dele. */
  const peleDoParado = enviando
    ? { background: envio.fundo, color: envio.letra, border: "none", cursor: "progress" }
    : travado
      ? { border: "none", cursor: "default" }
      : undefined;
  return (
    <div
      /* `--composer` mora aqui: clipe, microfone e envio leem a mesma variável
         em classe (`size-[var(--composer)]`) em vez de seis estilos calculados. */
      style={{ "--composer": celular ? "var(--altura-toque)" : "var(--altura-padrao)" }}
      className={`flex flex-col gap-1 rounded-superficie border bg-card p-[8px_8px_8px_12px] shadow-[0_3px_14px_color-mix(in_oklab,_var(--color-foreground)_4%,_transparent)] transition-[border-color,box-shadow] duration-[var(--t-curta)] ease-entra ${tomDaBorda} ${FOCO[tomDoEnvio] ?? FOCO.acao} ${oculto ? "hidden" : ""}`}
      hidden={oculto}
      {...resto}
    >
      {acima}

      {/* ---------------------------------------- em cima: só o texto */}
      <Textarea
        ref={ref}
        value={valor}
        onChange={(e) => aoMudar(e.target.value, e)}
        onKeyDown={(e) => {
          /* O dono do campo fala primeiro (lista do `/`, Esc que desfaz
             citação): se ele tratou a tecla, `preventDefault` já disse. */
          aoTeclar?.(e);
          if (e.defaultPrevented) return;
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (!travado) aoEnviar();
          }
        }}
        placeholder={convite}
        aria-label={rotulo}
        rows={1}
        /* Uma linha que cresce até caber umas cinco: `rows={2}` fixo deixava vão
           vazio embaixo de "ok" e, do outro lado, obrigava a rolar um parágrafo
           dentro de duas linhas. O `min-h-0` desfaz as quatro linhas que o
           `Textarea` do preset reserva — aqui a altura é do conteúdo. */
        className="max-h-[110px] min-h-0 sm:max-h-[150px] min-w-0 flex-1 resize-none overflow-y-auto rounded-none border-none bg-transparent p-[6px_8px] sm:p-[10px_8px] text-forte leading-[1.5] sm:text-corpo text-foreground shadow-none ring-0 focus-visible:border-none focus-visible:ring-0"
        {...campoProps}
      />

      {/* ---------------------------------------- embaixo: a barra */}
      <div className="flex items-center gap-1">
        {esquerda}
        {/* O vão: os ícones à esquerda, o que manda à direita, e nada escrito
            no meio. */}
        <span className="min-w-0 flex-1" />
        {direita}
        <Button
          onClick={aoEnviar}
          disabled={travado}
          data-dica={dicaDoEnvio}
          aria-label={dicaDoEnvio}
          className={`size-[var(--composer)] flex-none rounded-full border-none p-0 transition-[background-color,color,transform] duration-[var(--t-curta)] ease-entra ${envio.pele}`}
          style={peleDoParado}
        >
          {/* Saindo, o botão VIRA a espera: sem isto o único sinal de que a
              mensagem está em voo era a dica, que ninguém lê no meio do envio.
              A chave é o destino: trocar para nota monta o ícone de novo, e ele
              entra crescendo em vez de saltar de avião para bloco de notas — a
              troca de destino é a coisa que não pode passar despercebida. */}
          {enviando ? (
            <Spinner className="size-4" />
          ) : (
            <span
              key={tomDoEnvio}
              className="grid place-items-center animate-in fade-in zoom-in-50 duration-[var(--t-curta)] ease-entra"
            >
              <Icone de={iconeDoEnvio} tamanho={16} />
            </span>
          )}
        </Button>
      </div>
    </div>
  );
});
